import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePeopleFunnel } from '@/hooks/useConsoleData';
import { biggestDrop, eventKindLabel, type FunnelWindow, type MilestoneFunnelWindow } from '@/lib/people-data';
import { num, when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * The drop-off table at the top of People: who reached each step in the
 * last 7 and 30 days, how many were lost between steps, and the three
 * steps where the most people are sitting right now, each with the most
 * common last thing that happened to them (a hint of why). Every number is
 * a count of accounts; a step nobody reached prints 0 because 0 was
 * counted, and a percent with nobody before it prints nothing.
 *
 * The third box is why: for the window's people with no first check, how
 * many stopped for each reason (pv-auth reads the client's attempt reports
 * and PostHog for up to 300 of them per window). The people it could not
 * read are counted as not measured, never folded into a reason. Each row
 * links to the list with that stuck reason as the filter.
 */
export function FunnelPanel() {
  const funnel = usePeopleFunnel();
  const [days, setDays] = useState<7 | 30>(7);
  const f = funnel.data;
  const w: FunnelWindow | undefined = f?.windows.find((x) => x.days === days);
  const m: MilestoneFunnelWindow | undefined = f?.milestones?.find((x) => x.days === days);
  const noFirstCheck = m ? m.stuckMeasured + m.stuckNotMeasured : null;
  const worst = w ? biggestDrop(w) : null;
  const max = w ? Math.max(1, ...w.steps.map((x) => x.reached)) : 1;

  return (
    <div className={s.section} style={{ padding: '14px 16px' }} data-panel="funnel">
      <div className={s.panelHead}>
        <div>
          <div className={s.sectionTitle}>Where people stop</div>
          <div className={s.sub}>
            {w ? `${num(w.cohort)} signed up in the last ${days} days${worst ? ` · biggest loss: ${num(worst.dropped)} between ${w.steps[w.steps.indexOf(worst) - 1]?.label} and ${worst.label}` : ''} · refreshed ${when(f?.generatedAt ?? null)}` : funnel.isError ? 'This panel needs GET /provider/admin/people/funnel.' : 'Counting.'}
          </div>
        </div>
        <div className={s.filters} role="tablist" aria-label="Funnel window">
          {([7, 30] as const).map((d) => (
            <button key={d} type="button" role="tab" aria-selected={days === d} className={`${s.filter} ${days === d ? s.filterOn : ''}`} onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </div>
      </div>
      {w && (
        <div className={s.funnelGrid}>
          <div className={s.funnelSteps} role="table" aria-label="Funnel steps">
            {w.steps.map((step, i) => (
              <Link key={step.step} to={`/people?stage=${step.step}`} className={`${s.funnelRow} ${worst?.step === step.step ? s.funnelWorst : ''}`} role="row" title={`Show everyone whose stage is ${step.label}`}>
                <span className={s.funnelLabel}>{step.label}</span>
                <span className={s.funnelBarWrap}><span className={s.funnelBar} style={{ width: `${Math.round((step.reached / max) * 100)}%` }} /></span>
                <span className={s.funnelN}>{num(step.reached)}</span>
                <span className={s.funnelDrop}>{i === 0 || step.dropped === null ? '' : step.dropped === 0 ? 'nobody lost' : `${num(step.dropped)} lost${step.droppedPct !== null ? ` (${step.droppedPct}%)` : ''}`}</span>
              </Link>
            ))}
          </div>
          <div className={s.funnelStops}>
            <div className={s.groupTitle}>Stopped at, right now</div>
            {w.stoppedAt.length === 0 && <div className={s.empty}>Nobody in this window is short of paid.</div>}
            {w.stoppedAt.map((st) => (
              <Link key={st.step} to={`/people?stage=${st.step}`} className={s.stopRow}>
                <span className={s.stopCount}>{num(st.count)}</span>
                <span>
                  <span className={s.stopLabel}>at {st.label}</span>
                  <span className={s.sub}>{st.lastEvent ? `last thing for ${st.lastEvent.count === st.count ? 'all' : num(st.lastEvent.count)} of them: ${eventKindLabel(st.lastEvent.kind)}` : 'no event recorded after signup'}</span>
                </span>
              </Link>
            ))}
          </div>
          <div className={s.funnelStops} data-panel="funnel-why">
            <div className={s.groupTitle}>Why, before the first check</div>
            {!m && <div className={s.empty}>The funnel did not return the milestone windows. This box needs pv-auth #321 or later.</div>}
            {m && noFirstCheck === 0 && <div className={s.empty}>Everyone who signed up in this window did a first check.</div>}
            {m && noFirstCheck !== null && noFirstCheck > 0 && (
              <>
                <div className={s.sub} style={{ marginBottom: 4 }}>{num(noFirstCheck)} with no first check · reason read for {num(m.stuckMeasured)}{m.stuckNotMeasured ? ` · ${num(m.stuckNotMeasured)} not measurable` : ''}</div>
                {m.stuckReasons.length === 0 && <div className={s.empty}>No reason could be read for any of them.</div>}
                {m.stuckReasons.map((r) => (
                  <Link key={r.reason} to={`/people?stuck=${r.reason}&pageSize=200`} className={s.whyRow} title={`Show this page's rows stuck on: ${r.label}`}>
                    <span className={s.stopCount}>{num(r.count)}</span>
                    <span><span className={s.stopLabel}>{r.label}</span></span>
                  </Link>
                ))}
              </>
            )}
          </div>
        </div>
      )}
      {f && f.unavailable.length > 0 && <div className={s.jWarn} style={{ marginTop: 10 }}>Not read this time: {f.unavailable.map((u) => u.source).join(', ')}. The counts above leave those out rather than guess.</div>}
    </div>
  );
}
